import { activePackAnswers } from "./active-questions";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import type { PackageFormData } from "./types";

// Every flat field the full brief can hold, by the step that asks it.
// Content answers live in the `profileAnswers` bag, not here; `bandNames`
// stays flat because the document parse writes it from the rider's
// artist name.
const EVENT_FIELDS: readonly (keyof PackageFormData)[] = [
	"eventType",
	"eventTypeOther",
	"eventName",
	"organization",
	"eventDate",
	"eventEndDate",
	"eventStartTime",
	"eventEndTime",
];

const CONTACT_FIELDS: readonly (keyof PackageFormData)[] = [
	"contactName",
	"contactEmail",
	"contactPhone",
	"preferredContactMethod",
	"contactWhen",
	"details",
];

const VENUE_FIELDS: readonly (keyof PackageFormData)[] = [
	"attendance",
	"attendanceBand",
	"location",
	// Pick-time geocode of the location (see AddressAutocomplete).
	"siteLat",
	"siteLng",
	"siteGeocodedLocation",
	"venueContact",
	"siteVisit",
	"siteVisitWhen",
	"indoorOutdoor",
	"powerAccess",
	"wetWeatherPlan",
	"hasStage",
	"stageDetails",
	"siteAvailableFrom",
	"packOutTime",
	"callTime",
];

function contentFields(profile: InquiryProfileConfig): (keyof PackageFormData)[] {
	return ["bandNames", profile.contentStep.additionalInfo.field];
}

/**
 * The single source of truth for conditional visibility, validation inputs,
 * and multipart payload inclusion. A hidden answer may stay in the local
 * draft so Back navigation is lossless, but it can never leave the browser.
 */
export function getActiveInquiryFields(
	profile: InquiryProfileConfig,
	formData: Partial<PackageFormData>,
): ReadonlySet<keyof PackageFormData> {
	const active = new Set<keyof PackageFormData>([
		...EVENT_FIELDS,
		...CONTACT_FIELDS,
		...VENUE_FIELDS,
		...contentFields(profile),
	]);
	if (formData.eventType !== "other") active.delete("eventTypeOther");
	if (formData.indoorOutdoor !== profile.venueStep.outdoor.when.equals) {
		active.delete("powerAccess");
		active.delete("wetWeatherPlan");
	}
	if (!formData.hasStage) active.delete("stageDetails");
	// A time for a visit nobody asked for is not an answer to anything.
	if (!formData.siteVisit) active.delete("siteVisitWhen");
	// When suits is the answer to a call or a text; a time to email
	// somebody is nothing.
	if (
		formData.preferredContactMethod !== "call" &&
		formData.preferredContactMethod !== "text"
	) {
		active.delete("contactWhen");
	}

	return active;
}

/**
 * What leaves the browser with the submission: the flat fields asked
 * right now and the answers to the questions asked right now. A hidden
 * answer may sit in the draft for Back, but never leaves it.
 */
export function activeInquiryFormData(
	profile: InquiryProfileConfig,
	formData: Partial<PackageFormData>,
	riderAttached: boolean,
): Partial<PackageFormData> {
	const active = getActiveInquiryFields(profile, formData);
	const result = Object.fromEntries(
		Object.entries(formData).filter(([field]) => active.has(field as keyof PackageFormData)),
	) as Partial<PackageFormData>;
	// `profileAnswers` is no flat field, so the filter above always drops
	// it, and the bag goes back on as its asked answers alone.
	const answers = activePackAnswers(profile, formData, riderAttached);
	if (Object.keys(answers).length > 0) result.profileAnswers = answers;
	return result;
}

/**
 * What the estimate is sent: the submission's answers, minus who the
 * Customer is. An allow-list of the steps that describe the job, so a
 * field added to the contact step never reaches the model by default.
 */
export function estimateInquiryFormData(
	profile: InquiryProfileConfig,
	formData: Partial<PackageFormData>,
	riderAttached: boolean,
): Partial<PackageFormData> {
	const allowed = new Set<keyof PackageFormData>([
		...EVENT_FIELDS,
		...VENUE_FIELDS,
		...contentFields(profile),
		"profileAnswers",
	]);
	return Object.fromEntries(
		Object.entries(activeInquiryFormData(profile, formData, riderAttached)).filter(
			([field]) => allowed.has(field as keyof PackageFormData),
		),
	) as Partial<PackageFormData>;
}
